import { useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';

export type TriviaOption = {
  id: string;
  question_id: string;
  option_text: string;
  is_correct: boolean;
  position: number;
};

export type TriviaQuestion = {
  id: string;
  quiz_id: string;
  question_text: string;
  position: number;
  options: TriviaOption[];
};

export type TriviaQuiz = {
  id: string;
  title: string;
  description: string | null;
  sermon_id: string | null;
  is_active: boolean;
  created_at: string;
};

export type TriviaBadgeEntry = {
  id: string;
  score: number;
  badge: string;
  user_id: string;
  user_name: string;
  user_avatar: string | null;
  previous_score?: number | null;
  score_delta?: number | null;
};

export type TriviaResultEntry = {
  id: string;
  score: number;
  badge: string;
  share_to_badge_list: boolean;
  submitted_at: string;
  quiz_id: string;
  quiz_title: string;
  quiz_created_at: string;
  user_id: string;
  user_name: string;
  user_avatar: string | null;
};

export const useTriviaAdmin = () =>
  useQuery({
    queryKey: ['trivia', 'admin', 'active'],
    queryFn: async () => {
      const { data: quiz, error: quizError } = await supabase
        .from('trivia_quizzes')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (quizError) throw quizError;

      if (!quiz) {
        return { quiz: null, questions: [] as TriviaQuestion[] };
      }

      const { data: questions, error: questionsError } = await supabase
        .from('trivia_questions')
        .select('id, quiz_id, question_text, position')
        .eq('quiz_id', quiz.id)
        .order('position', { ascending: true });
      if (questionsError) throw questionsError;

      const questionIds = (questions ?? []).map((q) => q.id);
      const { data: options, error: optionsError } = questionIds.length
        ? await supabase
            .from('trivia_options')
            .select('id, question_id, option_text, is_correct, position')
            .in('question_id', questionIds)
            .order('position', { ascending: true })
        : { data: [], error: null };
      if (optionsError) throw optionsError;

      const optionsByQuestion = new Map<string, TriviaOption[]>();
      (options ?? []).forEach((opt) => {
        const existing = optionsByQuestion.get(opt.question_id) ?? [];
        existing.push(opt as TriviaOption);
        optionsByQuestion.set(opt.question_id, existing);
      });

      const mappedQuestions = (questions ?? []).map((q) => ({
        ...(q as TriviaQuestion),
        options: optionsByQuestion.get(q.id) ?? [],
      }));

      return { quiz: quiz as TriviaQuiz, questions: mappedQuestions };
    },
    staleTime: 30_000,
  });

export const useTriviaBadgeListAdmin = (
  quizId?: string | null,
  quizCreatedAt?: string | null
) =>
  useQuery({
    queryKey: ['trivia', 'admin', 'badge-list', quizId, quizCreatedAt],
    queryFn: async () => {
      if (!quizId) return [] as TriviaBadgeEntry[];
      const { data, error } = await supabase
        .from('trivia_attempts')
        .select('id, score, badge, user_id, users (id, full_name, avatar)')
        .eq('quiz_id', quizId)
        .eq('share_to_badge_list', true)
        .order('score', { ascending: false });
      if (error) throw error;
      const entries = (data ?? []).map((entry: any) => ({
        id: entry.id,
        score: entry.score,
        badge: entry.badge,
        user_id: entry.user_id,
        user_name: entry.users?.full_name ?? 'Unknown User',
        user_avatar: entry.users?.avatar ?? null,
      })) as TriviaBadgeEntry[];

      if (!quizCreatedAt || entries.length === 0) {
        return entries;
      }

      const { data: previousQuiz, error: previousQuizError } = await supabase
        .from('trivia_quizzes')
        .select('id, created_at')
        .lt('created_at', quizCreatedAt)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (previousQuizError) throw previousQuizError;
      if (!previousQuiz) {
        return entries;
      }

      const userIds = entries.map((entry) => entry.user_id);
      const { data: previousAttempts, error: previousAttemptsError } = await supabase
        .from('trivia_attempts')
        .select('user_id, score')
        .eq('quiz_id', previousQuiz.id)
        .in('user_id', userIds);
      if (previousAttemptsError) throw previousAttemptsError;

      const previousScoreByUser = new Map<string, number>();
      (previousAttempts ?? []).forEach((attempt: any) => {
        previousScoreByUser.set(attempt.user_id, attempt.score);
      });

      return entries.map((entry) => {
        const previousScore = previousScoreByUser.get(entry.user_id);
        if (previousScore === undefined) {
          return { ...entry, previous_score: null, score_delta: null };
        }
        return {
          ...entry,
          previous_score: previousScore,
          score_delta: entry.score - previousScore,
        };
      }) as TriviaBadgeEntry[];
    },
    enabled: !!quizId,
    staleTime: 20_000,
  });

export const useTriviaResultsAdmin = (filters: {
  title?: string;
  startDate?: string;
  endDate?: string;
}) =>
  useQuery({
    queryKey: ['trivia', 'admin', 'results', filters],
    queryFn: async () => {
      let query = supabase
        .from('trivia_attempts')
        .select(
          'id, score, badge, share_to_badge_list, submitted_at, user_id, users (id, full_name, avatar), trivia_quizzes (id, title, created_at)'
        )
        .order('submitted_at', { ascending: false });

      if (filters.title?.trim()) {
        query = query.ilike('trivia_quizzes.title', `%${filters.title.trim()}%`);
      }
      if (filters.startDate) {
        query = query.gte('trivia_quizzes.created_at', filters.startDate);
      }
      if (filters.endDate) {
        query = query.lte('trivia_quizzes.created_at', filters.endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []).map((entry: any) => ({
        id: entry.id,
        score: entry.score,
        badge: entry.badge,
        share_to_badge_list: entry.share_to_badge_list ?? false,
        submitted_at: entry.submitted_at,
        quiz_id: entry.trivia_quizzes?.id ?? entry.quiz_id,
        quiz_title: entry.trivia_quizzes?.title ?? 'Untitled Quiz',
        quiz_created_at: entry.trivia_quizzes?.created_at ?? '',
        user_id: entry.user_id,
        user_name: entry.users?.full_name ?? 'Unknown User',
        user_avatar: entry.users?.avatar ?? null,
      })) as TriviaResultEntry[];
    },
    staleTime: 30_000,
  });
